import { API_BASE, getAccessToken } from "@/lib/api/session";
import { getLocale } from "@/i18n";

export type InsightSections = {
  idea: string;
  meaning: string;
  tryToday: string;
  inShort: string | null;
};

export type DailyInsight = {
  id: string;
  slug: string;
  dayIndex: number;
  week: number;
  category: string;
  suggestedExperimentSlug: string | null;
  title: string;
  body: string;
  takeaway: string;
  scienceHint: string | null;
  tryToday?: string;
  whyForYou?: string | null;
  sections?: InsightSections | null;
  locale: string;
};

export type DailyInsightResponse = {
  /** Catalog index only — not a course progress counter. Prefer not showing in UI. */
  dayIndex: number;
  /** Always null for user-facing progress (no fixed 28-day track). */
  total: number | null;
  personalized?: boolean;
  whyForYou?: string | null;
  insight: DailyInsight;
};

export type TrackResponse = {
  total: number;
  openedCount: number;
  openedIds: string[];
  items: DailyInsight[];
  libraryLocked?: boolean;
};

function localeQuery(): string {
  const loc = getLocale();
  if (loc === "sv") return "sv-SE";
  return loc;
}

function localCalendarDayKey(): string {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Stockholm",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function dailyInsightCacheKey(): string {
  return `andrum.dailyInsight.v3.${localCalendarDayKey()}`;
}

function readDailyInsightCache(): DailyInsightResponse | null {
  try {
    const raw = sessionStorage.getItem(dailyInsightCacheKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DailyInsightResponse;
    if (!parsed?.insight?.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeDailyInsightCache(res: DailyInsightResponse): void {
  try {
    sessionStorage.setItem(dailyInsightCacheKey(), JSON.stringify(res));
  } catch {
    /* ignore quota / private mode */
  }
}

export async function fetchDailyInsight(): Promise<DailyInsightResponse | null> {
  const cached = readDailyInsightCache();
  if (cached) return cached;

  try {
    const token = await getAccessToken();
    const res = await fetch(
      `${API_BASE}/api/v1/insights/daily?locale=${encodeURIComponent(localeQuery())}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!res.ok) return null;
    const data = (await res.json()) as DailyInsightResponse;
    if (data?.insight?.id) writeDailyInsightCache(data);
    return data;
  } catch {
    return null;
  }
}

export async function fetchInsightTrack(): Promise<TrackResponse | null> {
  try {
    const token = await getAccessToken();
    const res = await fetch(
      `${API_BASE}/api/v1/insights/track?locale=${encodeURIComponent(localeQuery())}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (!res.ok) return null;
    return (await res.json()) as TrackResponse;
  } catch {
    return null;
  }
}

export async function markInsightOpen(
  id: string,
  completed = false
): Promise<boolean> {
  try {
    const token = await getAccessToken();
    if (!token) return false;
    const res = await fetch(`${API_BASE}/api/v1/insights/${id}/open`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ completed }),
    });
    return res.ok;
  } catch {
    return false;
  }
}
